import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { formatPlaceName }from '@/lib/utils';

interface ChartDataItem {
  name: string;
  value: number;
  isSelected: boolean;
}

interface ComparisonBarChartProps {
  data: ChartDataItem[];
  yAxisLabel: string;
  tooltipFormatter: (value: number) => string;
  tooltipLabel?: string;
  selectedColor?: string;
  peerColor?: string;
  height?: number;
  labelFontSize?: number;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

const CustomTooltip = ({ active, payload, label, formatter, yAxisLabel, tooltipLabel }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const finalLabel = tooltipLabel || yAxisLabel.split('(')[0].trim();
    return (
      <div className="bg-white p-2 border border-gray-300 rounded shadow-lg">
        <p className="font-semibold">{formatPlaceName(label)}</p>
        <p><span className="font-normal">{finalLabel}:</span> {formatter(value)}</p>
      </div>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload, labelFontSize = 12 }: any) => {
  if (payload && payload.value) {
    const formattedLabel = formatPlaceName(payload.value);
    const maxLength = 15; // Keep labels concise
    const truncatedLabel =
      formattedLabel.length > maxLength
        ? `${formattedLabel.substring(0, maxLength)}...`
        : formattedLabel;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={2} textAnchor="end" fill="#666" transform="rotate(-45)" fontSize={labelFontSize}>
          {truncatedLabel}
        </text>
      </g>
    );
  }
  return null;
};

const YAxisLabelWithLineBreak = (props: any) => {
  const { value, viewBox, labelFontSize } = props;
  if (!viewBox) return null;

  const { x, y, width, height } = viewBox;
  const words = value.split(' ');
  const g_transform = `translate(${x - 20}, ${y + height / 2 + 20}) rotate(-90)`;

  const maxCharsPerLine = 21;
  const lines = words.reduce((acc, word) => {
    const lastLine = acc[acc.length - 1];
    if (lastLine && (lastLine + ' ' + word).length <= maxCharsPerLine) {
      acc[acc.length - 1] = lastLine + ' ' + word;
    } else {
      acc.push(word);
    }
    return acc;
  }, [] as string[]);

  const lineHeight = 1.2; // em
  const initialDy = -((lines.length - 1) * lineHeight) / 2;

  return (
    <g transform={g_transform}>
      <text textAnchor="middle" dominantBaseline="central" fill="#666" fontSize={labelFontSize}>
        {lines.map((line, index) => (
          <tspan x="0" dy={index === 0 ? `${initialDy}em` : `${lineHeight}em`} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export const ComparisonBarChart: React.FC<ComparisonBarChartProps> = ({
  data,
  yAxisLabel,
  tooltipFormatter,
  tooltipLabel,
  selectedColor = '#ca0270',
  peerColor = '#d1d5db', // gray-300
  height = 300,
  labelFontSize = 14,
  margin = {
    top: 15,
    right: 20,
    left: 40,
    bottom: 30,
  },
}) => {
  if (!data || data.length === 0) {
    return <div>No data to display.</div>;
  }

  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.abs(max - min) * 0.1 || Math.abs(max) * 0.1 || 1;

  const yAxisMin = min - padding;
  const yAxisMax = max + padding;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        margin={margin}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" height={60} interval={0} tick={<CustomXAxisTick labelFontSize={labelFontSize} />} />
        <YAxis 
          domain={[yAxisMin, yAxisMax]}
          tickFormatter={(tick) => {
            if (typeof tick === 'number') {
              return tick.toFixed(2);
            }
            return tick;
          }}
          label={<YAxisLabelWithLineBreak value={yAxisLabel} labelFontSize={labelFontSize} />}
        />
        <Tooltip content={<CustomTooltip formatter={tooltipFormatter} yAxisLabel={yAxisLabel} tooltipLabel={tooltipLabel} />} cursor={{ fill: 'rgba(209, 213, 219, 0.3)' }} />
        <Bar dataKey="value" animationDuration={300}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.isSelected ? selectedColor : peerColor} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}; 